import React, { useState } from 'react'
import { Button, CircularProgress } from '@mui/material'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import html2canvas from 'html2canvas'
import { PdfExportSection } from 'interfaces/ISummaryReport'
import toast from '../toast'
import Tooltip from '../tooltip'

interface IPdfExportProps {
  sections: Array<PdfExportSection>
  disabled?: boolean
  carrierInfoRef?: React.RefObject<HTMLDivElement>
  filename?: string
  reportTitle?: string
  subtitle?: string
  disclaimer?: string
  testId?: string
}

const PdfExport: React.FC<IPdfExportProps> = ({
  sections,
  disabled = false,
  carrierInfoRef,
  filename = 'report.pdf',
  reportTitle,
  subtitle,
  disclaimer,
  testId,
}) => {
  const [isExporting, setIsExporting] = useState(false)
  const carrierName = localStorage.getItem('carrierName') || ''
  const companyName = localStorage.getItem('companyName') || ''

  const exportToPDF = async () => {
    setIsExporting(true)

    try {
      // 1. PDF & Page Setup
      const pdf = new jsPDF('l', 'mm', 'a4') // Landscape, millimeters, A4 size
      const pageDimensions = pdf.internal.pageSize
      const pageWidth = pageDimensions.getWidth()
      const pageHeight = pageDimensions.getHeight()

      const margin = 20
      const maxUsableWidth = pageWidth - margin * 2
      const disclaimerHeight = 25
      const maxUsableHeight = pageHeight - margin
      const maxUsableHeightFirstPage = disclaimer
        ? pageHeight - margin - disclaimerHeight
        : maxUsableHeight
      let yPosition = margin
      let isFirstPage = true

      // 2. Main Report Title
      pdf.setFontSize(20)
      pdf.setFont('helvetica', 'bold')
      pdf.setTextColor(40, 40, 40)

      const maxTitleWidth = maxUsableWidth

      const titleText = reportTitle || (carrierName ? `${carrierName} - Report` : 'Report')

      pdf.text(titleText, margin, yPosition, { maxWidth: maxTitleWidth })

      const titleLines = pdf.splitTextToSize(titleText, maxTitleWidth)
      const titleHeight = titleLines.length * 7
      yPosition += titleHeight + 3

      // 2b. Company Name
      if (companyName) {
        pdf.setFontSize(14)
        pdf.setFont('helvetica', 'bold')
        pdf.setTextColor(60, 60, 60)
        pdf.text(companyName, margin, yPosition, { maxWidth: maxTitleWidth })

        const companyNameLines = pdf.splitTextToSize(companyName, maxTitleWidth)
        const companyNameHeight = companyNameLines.length * 5
        yPosition += companyNameHeight + 5
      }

      // 2c. Subtitle with generation date
      const generatedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      const generatedTime = new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
      pdf.setFontSize(10)
      pdf.setFont('helvetica', 'normal')
      pdf.setTextColor(100, 100, 100)
      const subtitleText = subtitle
        ? `${subtitle}  ·  Generated on ${generatedDate} at ${generatedTime}`
        : `Generated on ${generatedDate} at ${generatedTime}`
      pdf.text(subtitleText, margin, yPosition, { maxWidth: maxTitleWidth })

      const subtitleLines = pdf.splitTextToSize(subtitleText, maxTitleWidth)
      const subtitleHeight = subtitleLines.length * 4
      yPosition += subtitleHeight + 10

      // 3. Capture and add Carrier Info section (if ref exists)
      if (carrierInfoRef?.current) {
        try {
          const canvas = await html2canvas(carrierInfoRef.current, {
            scale: 3,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
          })
          const imgData = canvas.toDataURL('image/jpeg', 0.9)
          const imgWidth = maxUsableWidth
          const imgHeight = (canvas.height * imgWidth) / canvas.width

          const currentMaxHeight = isFirstPage
            ? maxUsableHeightFirstPage
            : maxUsableHeight
          if (yPosition + imgHeight > currentMaxHeight) {
            pdf.addPage()
            yPosition = margin
            isFirstPage = false
          }

          pdf.addImage(imgData, 'JPEG', margin, yPosition, imgWidth, imgHeight)
          yPosition += imgHeight + 15
        } catch {}
      }

      // 3b. Add Disclaimer Footer on First Page (if provided)
      if (disclaimer) {
        const disclaimerY = pageHeight - margin - 15
        pdf.setFontSize(7)
        pdf.setFont('helvetica', 'normal')
        pdf.setTextColor(120, 120, 120)

        pdf.setDrawColor(200, 200, 200)
        pdf.setLineWidth(0.5)
        pdf.line(margin, disclaimerY - 5, pageWidth - margin, disclaimerY - 5)

        const disclaimerLines = pdf.splitTextToSize(disclaimer, maxUsableWidth)
        pdf.text(disclaimerLines, margin, disclaimerY, {
          maxWidth: maxUsableWidth,
          align: 'justify',
        })
      }

      // 4. Loop Through All Sections
      for (const section of sections) {
        const currentMaxHeight = isFirstPage
          ? maxUsableHeightFirstPage
          : maxUsableHeight
        if (yPosition + 30 > currentMaxHeight) {
          pdf.addPage()
          yPosition = margin
          isFirstPage = false
        }

        if (section.title) {
          pdf.setFontSize(14)
          pdf.setTextColor(50, 50, 50)
          pdf.text(section.title, margin, yPosition)
          yPosition += 10
        }

        // Handle VISUAL Sections
        if (
          section.type === 'visual' &&
          section.refs &&
          section.refs.length > 0
        ) {
          const columns = section.gridColumns || 1
          const cardPadding = columns > 1 ? 10 : 0
          const cardWidth =
            (maxUsableWidth - cardPadding * (columns - 1)) / columns

          const captureCard = async (ref: React.RefObject<HTMLDivElement>) => {
            if (!ref.current) return null
            const canvas = await html2canvas(ref.current, {
              scale: 3,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              logging: false,
            })
            const imgData = canvas.toDataURL('image/jpeg', 0.9)
            const imgHeight = (canvas.height * cardWidth) / canvas.width
            return { imgData, imgHeight }
          }

          const capturedCards = await Promise.all(section.refs.map(captureCard))
          const validCards = capturedCards.filter(Boolean)

          let i = 0
          while (i < validCards.length) {
            const rowCards = []
            let rowMaxHeight = 0

            for (let j = 0; j < columns && i < validCards.length; j++, i++) {
              const card = validCards[i]
              rowCards.push(card)
              if (card && card.imgHeight > rowMaxHeight) {
                rowMaxHeight = card?.imgHeight || 0
              }
            }

            const currentMaxHeight = isFirstPage
              ? maxUsableHeightFirstPage
              : maxUsableHeight
            if (yPosition + rowMaxHeight > currentMaxHeight) {
              pdf.addPage()
              yPosition = margin
              isFirstPage = false
            }

            let xPos = margin
            for (const card of rowCards) {
              pdf.addImage(
                card?.imgData || '',
                'JPEG',
                xPos,
                yPosition,
                cardWidth,
                card?.imgHeight || 0
              )
              xPos += cardWidth + cardPadding
            }

            yPosition += rowMaxHeight + cardPadding
          }

          yPosition += 5

          // Handle TABLE Sections
        } else if (section.type === 'table' && section.data) {
          autoTable(pdf, {
            head: [section.columns || []],
            body: section.data,
            startY: yPosition,
            margin: { left: margin, right: margin },
            styles: {
              fontSize: 8,
              cellPadding: 3,
              overflow: 'linebreak',
            },
            headStyles: {
              fillColor: [66, 139, 202],
              textColor: 255,
              fontStyle: 'bold',
            },
            alternateRowStyles: {
              fillColor: [245, 245, 245],
            },
          })

          yPosition = (pdf as any).lastAutoTable.finalY + 10
        }
      }

      pdf.save(filename)
      toast({
        type: 'success',
        title: 'PDF generated successfully',
      })
    } catch (error) {
      toast({
        type: 'error',
        title: 'Error generating PDF',
      })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Tooltip
      title={
        disabled ? 'PDF export will be available when all data is loaded' : ''
      }
      placement="bottom"
    >
      <span>
        <Button
          variant="contained"
          color="primary"
          onClick={exportToPDF}
          disabled={isExporting || disabled}
          startIcon={isExporting ? <CircularProgress size={20} /> : null}
          data-testid={testId}
        >
          {isExporting ? 'Generating PDF...' : 'Export to PDF'}
        </Button>
      </span>
    </Tooltip>
  )
}

export default PdfExport
